import {
  SOA_REASONS,
  type EvidenceFile,
  type Paginated,
  type ResponseSummaryItem,
  type Soa,
  type SoaControlRow,
  type SoaReason,
  type SoaReasonEntry,
  type SoaStatus,
  type SoaSummary,
  type UpdateSoaControlInput,
} from '@grc/types';
import { ApiRequestError, apiFetch } from '../../lib/api.js';

/**
 * Pure helpers behind the Statement of Applicability page: the local edits
 * map (spec 3.6), the values a row displays, reason chips, the read-only
 * evidence / response-status column derived from the chosen assessment, and
 * the 400-issue mapping shown inline under the fields.
 */

/** A row's unsaved changes: only the fields the user touched. */
export type SoaEdit = Partial<UpdateSoaControlInput>;
/** clauseId → unsaved changes. */
export type SoaEdits = Record<string, SoaEdit>;

/** The editable columns as the row shows them (local edit over the server row). */
export interface SoaValues {
  applicable: boolean;
  reasons: SoaReasonEntry[];
  justification: string;
  implementation: string;
  implementationStatus: SoaStatus;
  statusNote: string;
  ownerUserId: string | null;
  evidenceNote: string;
}

export function valuesOf(row: SoaControlRow, edit: SoaEdit | undefined): SoaValues {
  return {
    applicable: edit?.applicable ?? row.applicable,
    reasons: edit?.reasons ?? row.reasons,
    justification: edit?.justification ?? row.justification,
    implementation: edit?.implementation ?? row.implementation,
    implementationStatus: edit?.implementationStatus ?? row.implementationStatus,
    statusNote: edit?.statusNote ?? row.statusNote,
    ownerUserId: edit?.ownerUserId === undefined ? row.ownerUserId : edit.ownerUserId,
    evidenceNote: edit?.evidenceNote ?? row.evidenceNote,
  };
}

export function isDirty(edit: SoaEdit | undefined): boolean {
  return !!edit && Object.keys(edit).length > 0;
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Merge a patch into a row's edit. A patched value equal to the server value
 * is dropped again (ticking and unticking leaves the row clean); `undefined`
 * removes a key. Returns undefined when nothing differs from the server.
 */
export function mergeEdit(
  row: SoaControlRow,
  edit: SoaEdit | undefined,
  patch: SoaEdit,
): SoaEdit | undefined {
  const next: Record<string, unknown> = { ...(edit ?? {}) };
  const server = row as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || same(value, server[key])) delete next[key];
    else next[key] = value;
  }
  return Object.keys(next).length > 0 ? (next as SoaEdit) : undefined;
}

/**
 * After the server rows change (a refetch following a save), drop the edits
 * that no longer differ and the edits of rows that disappeared. Returns the
 * same object when nothing changed so memoised rows keep their props.
 */
export function pruneEdits(edits: SoaEdits, rowById: ReadonlyMap<string, SoaControlRow>): SoaEdits {
  let changed = false;
  const out: SoaEdits = {};
  for (const [clauseId, edit] of Object.entries(edits)) {
    const row = rowById.get(clauseId);
    if (!row) {
      changed = true;
      continue;
    }
    const merged = mergeEdit(row, undefined, edit);
    if (!merged) {
      changed = true;
      continue;
    }
    if (Object.keys(merged).length === Object.keys(edit).length) {
      out[clauseId] = edit;
    } else {
      changed = true;
      out[clauseId] = merged;
    }
  }
  return changed ? out : edits;
}

export function without<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

// ------------------------------------------------------------------ reasons

export const REASON_PLACEHOLDERS: Record<SoaReason, string> = {
  'Business Requirement': 'e.g. ISO/IEC 27001 clause 5.2 and certification scope',
  'Contractual Requirement': 'e.g. client contracts, data processing agreements and security questionnaires',
  'Legal Requirement': 'e.g. UK GDPR and the Data Protection Act 2018',
  'Risk Management Requirement': 'e.g. R49, R52',
};

const REASON_SHORT: Record<SoaReason, string> = {
  'Business Requirement': 'Business',
  'Contractual Requirement': 'Contractual',
  'Legal Requirement': 'Legal',
  'Risk Management Requirement': 'Risk management',
};

/** The compact row's reasons cell: "Not applicable" for an excluded control. */
export function reasonsSummary(values: SoaValues): string {
  if (!values.applicable) return 'Not applicable';
  if (values.reasons.length === 0) return '—';
  return values.reasons.map((entry) => REASON_SHORT[entry.reason]).join(', ');
}

/** Tick or untick a reason, keeping SOA_REASONS order and the other details. */
export function toggleReason(
  reasons: ReadonlyArray<SoaReasonEntry>,
  reason: SoaReason,
  on: boolean,
): SoaReasonEntry[] {
  const byReason = new Map(reasons.map((entry) => [entry.reason, entry]));
  if (on) {
    if (!byReason.has(reason)) byReason.set(reason, { reason, detail: '' });
  } else {
    byReason.delete(reason);
  }
  return SOA_REASONS.filter((key) => byReason.has(key)).map((key) => byReason.get(key)!);
}

export function setReasonDetail(
  reasons: ReadonlyArray<SoaReasonEntry>,
  reason: SoaReason,
  detail: string,
): SoaReasonEntry[] {
  return reasons.map((entry) => (entry.reason === reason ? { reason, detail } : entry));
}

// ---------------------------------------------------------- derived column

export const NO_ASSESSMENT = 'No assessment';

/** What the export's Evidence and Response status columns say for one control. */
export interface SoaLinkSummary {
  evidenceFiles: string[];
  responseStatus: string;
}

const STATUS_WORDS: Record<string, string> = {
  not_started: 'not started',
  in_progress: 'in progress',
  drafted: 'drafted',
  under_review: 'under review',
  changes_requested: 'changes requested',
  finalised: 'finalised',
  assessed: 'assessed',
  locked: 'locked',
};

/** Mirrors the worker's summary: one word when every response shares a status, else a progress line. */
export function summariseResponseStatus(statuses: ReadonlyArray<string>): string {
  if (statuses.length === 0) return 'No questions';
  const distinct = new Set(statuses);
  if (distinct.size === 1) {
    const only = statuses[0]!;
    if (only !== 'in_progress') return STATUS_WORDS[only] ?? only.replaceAll('_', ' ');
  }
  const answered = statuses.filter((status) => status !== 'not_started').length;
  if (answered === 0) return STATUS_WORDS.not_started!;
  return `in progress (${answered} of ${statuses.length} answered)`;
}

/** Per control clause: the unique evidence file names on its responses and their status summary. */
export function buildLinkSummaries(
  responses: ReadonlyArray<ResponseSummaryItem>,
  evidence: ReadonlyArray<EvidenceFile>,
): Map<string, SoaLinkSummary> {
  const filesByResponse = new Map<string, string[]>();
  for (const file of evidence) {
    if (!file.responseId) continue;
    if (file.retentionStatus !== 'active' && file.retentionStatus !== 'retained') continue;
    const list = filesByResponse.get(file.responseId) ?? [];
    list.push(file.fileName);
    filesByResponse.set(file.responseId, list);
  }
  const byClause = new Map<string, { statuses: string[]; files: Set<string> }>();
  for (const response of responses) {
    const entry = byClause.get(response.clauseId) ?? { statuses: [], files: new Set<string>() };
    entry.statuses.push(response.status);
    for (const name of filesByResponse.get(response.id) ?? []) entry.files.add(name);
    byClause.set(response.clauseId, entry);
  }
  const out = new Map<string, SoaLinkSummary>();
  for (const [clauseId, entry] of byClause) {
    out.set(clauseId, {
      evidenceFiles: [...entry.files],
      responseStatus: summariseResponseStatus(entry.statuses),
    });
  }
  return out;
}

/** The compact row's evidence / response status text. */
export function linkText(
  link: SoaLinkSummary | undefined,
  hasAssessment: boolean,
  ready: boolean,
): string {
  if (!hasAssessment) return NO_ASSESSMENT;
  if (!ready) return 'Loading…';
  if (!link) return 'No questions';
  const files = link.evidenceFiles.length;
  const evidence = files === 0 ? 'No evidence attached' : `${files} evidence file${files === 1 ? '' : 's'}`;
  return `${evidence} · ${link.responseStatus}`;
}

/** Every page of a paginated list, bounded so a runaway total cannot loop. */
export async function fetchAllPages<T>(
  path: string,
  query: Record<string, string | number | undefined>,
  pageSize: number,
  maxPages: number,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 0; page < maxPages; page += 1) {
    const res = await apiFetch<Paginated<T>>(path, {
      query: { ...query, limit: pageSize, offset: page * pageSize },
    });
    items.push(...res.items);
    if (res.items.length === 0 || items.length >= res.total) break;
  }
  return items;
}

// ----------------------------------------------------------------- filters

/** Free-text search over the columns a person would scan for. */
export function rowMatches(row: SoaControlRow, needle: string): boolean {
  const haystack = [
    row.clauseNumber,
    row.title,
    row.description,
    row.justification,
    row.implementation,
    row.statusNote,
    row.ownerName ?? '',
    row.evidenceNote,
    ...row.reasons.map((entry) => `${entry.reason} ${entry.detail}`),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

// ------------------------------------------------------------- cache merge

export function summariseRows(rows: ReadonlyArray<SoaControlRow>): SoaSummary {
  const summary: SoaSummary = {
    total: rows.length,
    applicable: 0,
    notApplicable: 0,
    implemented: 0,
    partiallyImplemented: 0,
    planned: 0,
  };
  for (const row of rows) {
    if (row.applicable) summary.applicable += 1;
    else summary.notApplicable += 1;
    if (row.implementationStatus === 'implemented') summary.implemented += 1;
    else if (row.implementationStatus === 'partially_implemented') summary.partiallyImplemented += 1;
    else if (row.implementationStatus === 'planned') summary.planned += 1;
  }
  return summary;
}

/** The cached SOA with saved rows swapped in (so a save never flickers back to the old text). */
export function withSavedRows(soa: Soa, saved: ReadonlyArray<SoaControlRow>): Soa {
  const byId = new Map(saved.map((row) => [row.clauseId, row]));
  const controls = soa.controls.map((row) => byId.get(row.clauseId) ?? row);
  return { ...soa, controls, summary: summariseRows(controls) };
}

// ------------------------------------------------------------------ errors

export interface ApiIssue {
  path: string;
  message: string;
}

/** The zod-style issues of a 400 validation_failed response (empty otherwise). */
export function issuesOf(err: unknown): ApiIssue[] {
  if (!(err instanceof ApiRequestError) || err.code !== 'validation_failed') return [];
  const issues = err.details?.issues;
  if (!Array.isArray(issues)) return [];
  return issues
    .filter(
      (issue): issue is { path?: unknown; message: string } =>
        !!issue && typeof issue === 'object' && typeof (issue as { message?: unknown }).message === 'string',
    )
    .map((issue) => ({ path: typeof issue.path === 'string' ? issue.path : '', message: issue.message }));
}

/** field → message; the first message per field wins. */
export function fieldErrors(issues: ReadonlyArray<ApiIssue>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    const field = issue.path.split('.').pop() || 'form';
    if (!(field in out)) out[field] = issue.message;
  }
  return out;
}

/** Bulk issues arrive as `controls.<index>.<field>`: group them per submitted row. */
export function bulkFieldErrors(
  issues: ReadonlyArray<ApiIssue>,
  clauseIdAt: (index: number) => string | undefined,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const issue of issues) {
    const match = /^controls\.(\d+)\.(.+)$/.exec(issue.path);
    if (!match) continue;
    const clauseId = clauseIdAt(Number(match[1]));
    if (!clauseId) continue;
    const field = match[2]!.split('.').pop()!;
    const row = out[clauseId] ?? {};
    if (!(field in row)) row[field] = issue.message;
    out[clauseId] = row;
  }
  return out;
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? err.message : fallback;
}

// ------------------------------------------------------------------- dates

/** Today as dd/mm/yyyy — the form the exported workbook uses. */
export function ukToday(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${now.getFullYear()}`;
}

/** Today as YYYY-MM-DD for date inputs. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
